import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Tag } from "primereact/tag";
import React from "react";
import { apiErrorDetail } from "../../../api/errors";
import { enqueueSindriReplacementCost } from "../../../api/routes";
import { ReportJobBatchEntry } from "../../../client/types.gen";
import { useToast } from "../../../context-providers/Toast";
import { formatDuration } from "../icm/util";
import { jobKeys } from "../queryKeys";
import { ensureUtc, formatTimestamp } from "../utils";
import { useReportJobs } from "../rater/useIntegrationJobs";
import styles from "../rater/RaterCard.module.css";
import {
  SINDRI_RUN_STATUS_LABELS,
  SINDRI_RUN_STATUS_SEVERITIES,
  sindriRunStatus,
} from "./sindriRunStatus";

type SindriReplacementCostTabProps = {
  reportId: string;
};

// How the account was scored, as the task reports it.
const PATH_LABEL: Record<string, string> = {
  sync: "Synchronous",
  job: "Sindri job",
};

/** Runs Sindri on demand and lists this report's replacement-cost runs. */
export const SindriReplacementCostTab: React.FC<
  SindriReplacementCostTabProps
> = ({ reportId }) => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { jobs } = useReportJobs(reportId, {
    integrationTypes: ["SINDRI_REPLACEMENT_COST"],
  });
  const sortedJobs = [...jobs].sort(
    (a, b) =>
      new Date(ensureUtc(b.started_at)).getTime() -
      new Date(ensureUtc(a.started_at)).getTime()
  );
  const runInProgress = sortedJobs.some(
    (job) => sindriRunStatus(job) === "in_progress"
  );

  const enqueue = useMutation({
    mutationFn: () => enqueueSindriReplacementCost(reportId),
    onSuccess: () => {
      toast.show({
        severity: "info",
        summary: "Replacement Cost Started",
        detail: "Sindri is scoring this report's locations.",
      });
      queryClient.invalidateQueries({ queryKey: jobKeys.reportJobs(reportId) });
    },
    onError: (error) => {
      toast.show({
        severity: "error",
        summary: "Replacement Cost Failed to Start",
        detail: apiErrorDetail(error) ?? "Could not start the Sindri run.",
      });
    },
  });

  return (
    <div className={styles.verticalContainerLargeGap}>
      <div>
        <Button
          label={
            sortedJobs.length > 0
              ? "Re-run replacement cost"
              : "Run replacement cost"
          }
          icon="pi pi-play"
          raised
          text
          severity="secondary"
          onClick={() => enqueue.mutate()}
          loading={enqueue.isPending}
          disabled={runInProgress}
          tooltip={runInProgress ? "A run is already in progress." : undefined}
          tooltipOptions={{ showOnDisabled: true, position: "top" }}
        />
        <p>
          Scores the report's enabled locations with Sindri in full mode. The
          results replace the Luminor RC columns on the SOV tab.
        </p>
      </div>
      {sortedJobs.length > 0 && (
        <DataTable value={sortedJobs} size="small">
          <Column
            header="Started"
            body={(job: ReportJobBatchEntry) => formatTimestamp(job.started_at)}
          />
          <Column
            header="Status"
            body={(job: ReportJobBatchEntry) => {
              const status = sindriRunStatus(job);
              return (
                <Tag
                  severity={SINDRI_RUN_STATUS_SEVERITIES[status]}
                  value={SINDRI_RUN_STATUS_LABELS[status]}
                />
              );
            }}
          />
          <Column
            header="Duration"
            body={(job: ReportJobBatchEntry) =>
              sindriRunStatus(job) === "superseded"
                ? "—"
                : formatDuration(job.started_at, job.finished_at)
            }
          />
          <Column
            header="Locations"
            body={(job: ReportJobBatchEntry) =>
              (job.jobs[0]?.response?.location_count as number | undefined) ??
              null
            }
          />
          <Column
            header="Scored by"
            body={(job: ReportJobBatchEntry) => {
              const path = job.jobs[0]?.response?.path as string | undefined;
              return path === undefined ? null : (PATH_LABEL[path] ?? path);
            }}
          />
        </DataTable>
      )}
    </div>
  );
};
